'use client'

import type { CSSProperties } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useTranslation } from '@/contexts/I18nContext'
import { getChatBubbleStyle } from '@/lib/constants/characterMessage'
import { escapeHtml, replaceUserPlaceholder } from '@/lib/utils/htmlEscape'
import { splitIntroSegments } from '@/lib/utils/introductionHtml'
import { useChatThemeColor } from '@/store/characterChatStore'
import { useChatUserName } from '@/store/profileCardStore'

interface IHtmlIntroductionCardProps {
  htmlData: string
}

// 隔离 iframe 的高度上报消息协议与高度上限（防止异常上报撑爆布局）
const HTML_CARD_MESSAGE_TYPE = 'htmlCardHeight'
const HTML_CARD_MAX_HEIGHT = 50000
// bootstrap 异常未上报时的兜底高度，避免卡片高度塌陷为 0
const HTML_CARD_FALLBACK_HEIGHT = 200

// iframe 自身样式：宽度占满父容器，高度由 JS 按内容自然高度设置。
const htmlContentStyles: CSSProperties = {
  width: '100%',
  background: 'transparent',
}

// 环境基线样式：只保留合成与可用性的功能性前提，内容样式一律以作者 HTML
// 自带的 CSS 为准（作者 <style> 位于 body，晚于本基线，同等优先级下作者胜出）：
// - transparent 背景：iframe 透明合成到应用 UI 上，不设则浏览器画 UA 默认底色；
// - margin:0 / border-box：嵌入式内容的标准前提，作者可自行覆盖；
// - 白色文字 + 应用字体栈：无样式内容在暗底 UI 上的可读性兜底。
const IFRAME_BASE_STYLES = `
  html,
  body {
    background: transparent;
  }

  body {
    margin: 0;
    color: #fff;
    font-family: Poppins, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 14px;
    line-height: 1.6;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`

// 沙箱文档 CSP：安全边界由 sandbox="allow-scripts" 的 opaque origin 承担（与主文档
// 无任何同源通道）。脚本放行内联执行（仅 html-box 段会携带脚本，需审核兜底），
// 作者 CSS 所需的字体与 blob 媒体放行；connect-src 保持 'none' 阻止脚本外联，
// worker-src 'none' 显式禁 Worker（防挖矿）；frame-src 放开以支持嵌套 iframe
// （嵌套 frame 继承 sandbox，同样完全隔离）。
const SANDBOX_CSP =
  "default-src 'none'; img-src http: https: data: blob:; media-src http: https: data: blob:; font-src http: https: data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; connect-src 'none'; object-src 'none'; worker-src 'none'; frame-src http: https: blob:; form-action 'none'; base-uri 'none'"

// 高度上报引导脚本：iframe 与主文档完全隔离（无 allow-same-origin），
// 父页面读不到 contentDocument，只能由内部把内容高度 postMessage 给父页面。
const HTML_CARD_BOOTSTRAP = `<script>
(function () {
  var raf = 0
  function post() {
    if (raf) return
    raf = requestAnimationFrame(function () {
      raf = 0
      var documentElement = document.documentElement
      var body = document.body
      if (!documentElement || !body) return
      var height = Math.max(
        documentElement.scrollHeight,
        body.scrollHeight,
        documentElement.offsetHeight,
        body.offsetHeight
      )
      window.parent.postMessage({ type: '${HTML_CARD_MESSAGE_TYPE}', height: height }, '*')
    })
  }
  document.addEventListener('DOMContentLoaded', function () {
    post()
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(post).observe(document.body)
    }
    window.addEventListener('resize', post)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(post)
  })
  /* load 事件不冒泡，用捕获阶段兜住 img/video 等资源异步加载完成后的高度变化 */
  document.addEventListener('load', post, true)
})()
</script>`

function buildSandboxedHtmlDocument(innerHtml: string, dir?: string | null): string {
  return `<!doctype html>
<html${dir ? ` dir="${dir}"` : ''}>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="referrer" content="no-referrer" />
    <meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}" />
    <style>${IFRAME_BASE_STYLES}</style>
    ${HTML_CARD_BOOTSTRAP}
  </head>
  <body>${innerHtml}</body>
</html>`
}

/**
 * HTML 段内容 iframe：sandbox 只有 allow-scripts（opaque origin），
 * 与主文档无任何 DOM 通道。高度由内部 bootstrap 脚本 postMessage 上报，
 * 父页面仅凭 event.source 校验消息来源（opaque origin 下 event.origin 为 "null"）。
 * 独立 memo：只在 html 内容变化时才重新渲染，展开/收起等父组件 state 变化
 * 不会触碰 srcDoc，图片不会重载。
 */
const HtmlContent = memo(({ html }: { html: string }) => {
  const { t } = useTranslation('character')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  // srcdoc 文档继承父文档方向（RTL 下作者内容方向正确）；
  // 本组件仅在客户端渲染（SSR 阶段外层返回错误占位），document 必然存在
  const srcDoc = useMemo(
    () =>
      buildSandboxedHtmlDocument(
        html,
        typeof document === 'undefined' ? null : document.documentElement.getAttribute('dir'),
      ),
    [html],
  )

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      const data = event.data as { type?: unknown; height?: unknown } | null
      if (!data || data.type !== HTML_CARD_MESSAGE_TYPE) return

      const height = data.height
      if (
        typeof height !== 'number' ||
        !Number.isFinite(height) ||
        height <= 0 ||
        height > HTML_CARD_MAX_HEIGHT
      ) {
        return
      }

      const iframe = iframeRef.current
      if (iframe) iframe.style.height = `${Math.ceil(height)}px`
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // 兜底：bootstrap 异常未上报时避免卡片高度塌陷为 0
  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe) return
    window.setTimeout(() => {
      if (iframeRef.current !== iframe) return
      if (!Number.parseInt(iframe.style.height, 10)) {
        iframe.style.height = `${HTML_CARD_FALLBACK_HEIGHT}px`
      }
    }, 1500)
  }, [])

  return (
    <iframe
      ref={iframeRef}
      title={t('Html.CharacterIntroduction')}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      className="block border-0 w-full"
      style={{ ...htmlContentStyles, height: 0 }}
    />
  )
})
HtmlContent.displayName = 'HtmlContent'

// 台词气泡/旁白/纯文本段样式：与聊天消息一致（固定 px，白字、保留换行）
const segmentTextStyle: CSSProperties = {
  color: '#fff',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
}

const HtmlIntroductionCard = memo(({ htmlData }: IHtmlIntroductionCardProps) => {
  const { t } = useTranslation('character')
  const username = useChatUserName()
  // <message> 气泡取色：与聊天助手气泡同源（角色背景图提取的 themeColor）
  const themeColor = useChatThemeColor()

  // {{user}} 占位符按段替换：文本段用原始用户名（React 渲染文本节点自动转义），
  // HTML 段用转义后的用户名（进入 srcdoc 文档）
  const segments = useMemo(() => {
    return splitIntroSegments(htmlData).map((segment) => {
      if (segment.type === 'html') {
        return {
          ...segment,
          html: replaceUserPlaceholder(segment.html, escapeHtml(username)),
        }
      }
      return { ...segment, text: replaceUserPlaceholder(segment.text, username) }
    })
  }, [htmlData, username])

  // 气泡样式与聊天页口径一致（themeColor 带 alpha 0.9，缺省走默认色）
  const bubbleStyle = useMemo(() => getChatBubbleStyle(themeColor), [themeColor])

  if (segments.length === 0) {
    return (
      <div className="p-4 rounded-lg border border-gray-700">
        <p className="text-gray-400">{t('Html.ContentFormatError')}</p>
      </div>
    )
  }

  // 组件本身不限制尺寸：内部 iframe 按内容自然宽高撑开，
  // 宽度约束与横向滚动交给调用方的外层容器处理。
  return (
    <ErrorBoundary>
      <div className="flex w-full flex-col gap-3">
        {segments.map((segment) => {
          if (segment.type === 'message') {
            return (
              <div
                key={segment.id}
                className="w-full px-4 py-3 text-[14px] leading-[1.6] whitespace-pre-wrap"
                style={{ ...bubbleStyle, ...segmentTextStyle }}
              >
                {segment.text}
              </div>
            )
          }
          if (segment.type === 'narration' || segment.type === 'text') {
            return (
              <div
                key={segment.id}
                className="w-full text-[14px] leading-[1.6] whitespace-pre-wrap"
                style={segmentTextStyle}
              >
                {segment.text}
              </div>
            )
          }
          return <HtmlContent key={segment.id} html={segment.html} />
        })}
      </div>
    </ErrorBoundary>
  )
})

HtmlIntroductionCard.displayName = 'HtmlIntroductionCard'

export default HtmlIntroductionCard
